import { useEffect, useMemo } from 'react';
import { useFeatureFlag } from '@hungryroot/feature-flags';
import { log } from '@hungryroot/o11y';
import { type Experiments } from '../experiments';
import { getExperimentGroup } from '../query/get-experiment-group';
import { useAnonymousWindowExperiments } from './useAnonymousWindowExperiments';
import { useClientSidePresignupExperiment } from './useClientSidePresignupExperiment';

export function usePresignupExperiment<T extends Experiments>(
  expId: T,
  { enabled }: { enabled: boolean }
) {
  const clientSideEnrollmentFlag = useFeatureFlag(
    'web.feature-flag.client-side-experiment-enrollment.enabled'
  );

  const clientSideExperiment = useClientSidePresignupExperiment(
    {
      exp: expId,
    },
    {
      enabled,
    }
  );

  const { data: anonymousUserExperiments } = useAnonymousWindowExperiments({
    enabled,
  });

  // if "useClientSidePresignupExperiment" returns null
  // that means that we couldn't enroll the user for
  // whatever reason (not variants defined, in active, whatever)
  // In that case, we will emit to the UI that the experiment variant is -1
  const clientSideExperimentVariant =
    clientSideExperiment === null ? -1 : clientSideExperiment;
  const windowExperimentVariant =
    anonymousUserExperiments !== undefined
      ? getExperimentGroup(expId, anonymousUserExperiments)
      : undefined;

  const variant = useMemo(() => {
    // still waiting for the feature flag to land
    if (clientSideEnrollmentFlag === undefined) {
      return undefined;
    }

    if (clientSideEnrollmentFlag === true) {
      return clientSideExperimentVariant;
    }

    return windowExperimentVariant;
  }, [
    clientSideEnrollmentFlag,
    clientSideExperimentVariant,
    windowExperimentVariant,
  ]);

  useEffect(() => {
    if (variant === undefined || enabled === false) {
      return;
    }

    if (variant === windowExperimentVariant) {
      // Log it to Datadog
      log('experiments.presignup.mismatched-emit', {
        experiment: expId,
        // stringifying these values so they show up
        // property in datadog
        variant,
        windowExperimentVariant,
        clientSideExperimentVariant,
      });
    }
  }, [
    variant,
    enabled,
    expId,
    clientSideExperimentVariant,
    windowExperimentVariant,
  ]);

  return variant;
}
